import { Bookmark } from '../../../../../typings/Bookmark.interface';
import { Event } from '../../../../../typings/Event.interface';
import { useContext } from 'react';
import APIMethodKeys from '../../client/APIMethodKeys';
import Text from '../common/base/Text';
import MultiSelectInput from '../common/Form/Fields/MultiSelectInput';
import TextInput from '../common/Form/Fields/TextInput';
import { useDialog } from '../common/Dialog';
import { useToasts } from '../common/Toast';
import { GlobalContext } from '../contexts/GlobalContext';
import { useIsQuotaEnforcementActive } from './useIsQuotaEnforcementActive';
import { StyledCard } from '../common/base/Card';
import { Status } from '../common/Status';
import Divider from '../common/base/Divider';
import { Div } from '../common/helpers/StyledUtils';
import Chip from '../common/base/Chip';
import useSWR from 'swr';
import { formatDate, getTimezoneAbbreviation } from '../../utils/date';
import Icon from '../common/base/Icon';
import { useDatePreferences } from './useDatePreferences';
const useEventAction = (callback: (action: 'retry' | 'bookmark' | 'cancel' | 'replay', event: Event, meta?: {
  bookmark?: Bookmark;
  events?: Event[];
}) => void): { [k in 'retry' | 'bookmark' | 'cancel' | 'replay']: (event: Event) => Promise<void> } => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const is_quota_enforcement_active = useIsQuotaEnforcementActive();
  const {
    timezone
  } = useDatePreferences();
  return {
    retry: async (event: Event) => {
      if (is_quota_enforcement_active) {
        addToast('error', 'Event retry disabled while plan is exceeded. Please upgrade to retry events.');
        return;
      }
      await HookdeckAPI.events.retry(event.id).then(result => {
        addToast('success', 'Event queued for retry');
        if (callback) callback('retry', result);
      }).catch(e => {
        if (e.response?.code === 'WEBHOOK_DISABLED' || e.response?.code === 'WEBHOOK_DELETED') {
          addToast('error', 'Failed to retry, connection disabled or deleted');
        } else {
          addToast('error', 'Failed to retry');
        }
      });
    },
    replay: async (event_0: Event) => {
      if (is_quota_enforcement_active) {
        addToast('error', 'Event replay disabled while plan is exceeded. Please upgrade to replay events.');
        return;
      }
      await new Promise(resolve => {
        showDialog(async (values: {
          webhook_ids: string[];
        }) => {
          await HookdeckAPI.events.replay(event_0.id, values.webhook_ids).then(result_0 => {
            const event_count = result_0.events?.length || 0;
            const request_count = result_0.requests?.length || 0;
            addToast('success', `Replay created: ${event_count} new event(s) across ${request_count} request(s)`);
            if (callback) callback('replay', event_0, {
              events: result_0.events
            });
          }).catch(error => {
            if (error.response?.code === 'DATA_ARCHIVED') {
              addToast('error', 'Failed to replay: event data is archived and no longer available');
            } else if (error.response?.code === 'NO_CONNECTIONS') {
              addToast('error', 'Failed to replay: no enabled connections in this project');
            } else if (error.response?.code === 'WEBHOOK_DISABLED' || error.response?.code === 'WEBHOOK_DELETED') {
              addToast('error', 'Failed to replay: connection is disabled or deleted');
            } else {
              addToast('error', 'Failed to replay event');
            }
          });
          resolve(null);
        }, () => resolve(null), {
          title: 'Replay event',
          submit_label: 'Replay',
          submit_icon: 'replay',
          cancel_label: 'Cancel',
          form_props: {
            initial_values: {
              webhook_ids: [event_0.webhook_id]
            },
            validate: ({
              webhook_ids
            }: {
              webhook_ids: string[];
            }) => {
              if (!webhook_ids || webhook_ids.length === 0) return {
                webhook_ids: 'Select at least one connection'
              };
              return {};
            },
            Fields: () => {
              const {
                data: webhook
              } = useSWR(APIMethodKeys.webhooks.get(event_0.webhook_id), () => HookdeckAPI.webhooks.get(event_0.webhook_id));
              const initial_options = webhook ? [{
                value: webhook.id,
                label: webhook.full_name
              }] : [];
              const formatOptions = data => data && data.models.map(m => ({
                value: m.id,
                label: m.full_name
              }));
              return <>
                    <Text>
                      Replay re-ingests the original request and runs it through the pipeline as if
                      it just arrived. The original event remains unchanged. Pick one or more
                      connections — a new request is created per target source.
                    </Text>
                    <Text semi muted size="xs" m={{
                  t: 4,
                  b: 1
                }}>
                      Original Event
                    </Text>
                    <StyledCard grid={{
                  columns: 1
                }}>
                      <Div p={3} flex={{
                    justify: 'space-between'
                  }}>
                        <Text muted ellipsis>
                          Event status
                        </Text>
                        <Status {...event_0} />
                      </Div>

                      <Divider />

                      <Div p={3} flex={{
                    justify: 'space-between'
                  }}>
                        <Text muted>Connection</Text>
                        {webhook ? <Chip outline mono label={webhook.full_name} to={`/connections/${webhook.id}`} /> : <Text muted shimmering>
                            Loading...
                          </Text>}
                      </Div>

                      <Divider />

                      <Div p={3} flex={{
                    justify: 'space-between'
                  }}>
                        <Text muted ellipsis>
                          Created at
                        </Text>
                        <Text mono ellipsis>
                          {formatDate(event_0.created_at, 'yyyy-MM-dd HH:mm:ss', timezone).toUpperCase()}{' '}
                          {getTimezoneAbbreviation(timezone, event_0.created_at)}
                        </Text>
                      </Div>

                      <Divider />

                      <Div p={3} flex={{
                    justify: 'space-between'
                  }}>
                        <Text muted ellipsis>
                          Event ID
                        </Text>
                        <Text mono ellipsis>
                          {event_0.id}
                        </Text>
                      </Div>
                    </StyledCard>
                    <Div w={100} flex={{
                  justify: 'center',
                  align: 'center'
                }} m={{
                  t: 6,
                  b: 4
                }}>
                      <Icon icon="arrow_downward" muted large />
                    </Div>
                    <MultiSelectInput name="webhook_ids" label="Target connections" option_monospace initial_options={initial_options} fetcher={HookdeckAPI.webhooks.list} fetcherKey={APIMethodKeys.webhooks.list} formatOptions={formatOptions} formatFilters={(search_term?: string, values_0?: string[]) => ({
                  limit: 250,
                  full_name: search_term ? search_term : undefined,
                  id: values_0
                })} required />
                  </>;
            }
          }
        });
      });
    },
    bookmark: async (event_1: Event) => {
      await new Promise(resolve_0 => {
        showDialog(async (values_1: {
          label: string;
        }) => {
          await HookdeckAPI.bookmarks.create({
            event_data_id: event_1.event_data_id,
            label: values_1.label,
            webhook_id: event_1.webhook_id
          }).then(bookmark => {
            if (callback) callback('bookmark', event_1, {
              bookmark
            });
            addToast('success', 'Event bookmarked');
          }).catch(error_0 => addToast('error', error_0.response?.code == 'DATA_ARCHIVED' ? 'Failed to create bookmark because associated data is archived' : 'Failed to bookmark event'));
          resolve_0(null);
        }, () => null, {
          title: 'Add bookmark',
          submit_label: 'Add',
          cancel_label: 'Cancel',
          form_props: {
            initial_values: {},
            validate: ({
              label
            }: {
              label: string;
            }) => {
              if (!label || label.length < 0) return {
                label: 'Required'
              };
              return {};
            },
            Fields: () => <TextInput name="label" label="Bookmark Label" placeholder="Shopify Order with Gift Card" m={{
              b: 2
            }} required focus />
          }
        });
      });
    },
    cancel: async (event_2: Event) => {
      await HookdeckAPI.events.cancel(event_2.id).then(updated_event => {
        addToast('success', 'Event delivery cancelled');
        if (callback) callback('cancel', updated_event);
      }).catch(() => addToast('error', 'Failed to cancel event'));
    }
  };
};
export default useEventAction;